import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { PlusIcon } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';
import { AnalysisEmptyState, analysesQuery } from '@/entities/analysis';
import { ROUTES } from '@/shared/config/routes';
import { ToolbarSkeleton } from '@/shared/ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useAnalysisCompletionDetection } from '../../model/useAnalyses';
import { AnalysisNotifications } from '../AnalysisNotifications';
import {
  AnalysisGridView,
  AnalysisListView,
  ViewToggle,
  type AnalysisViewMode,
} from './components';
import { useTranslation } from 'react-i18next';

export function AnalysisListContent() {
  const { t } = useTranslation('analysis');

  const navigate = useNavigate();
  const { data: analyses, isLoading, isError } = useQuery(analysesQuery());

  useAnalysisCompletionDetection(analyses);

  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState<AnalysisViewMode>('grid');

  const filteredRows = useMemo(() => {
    if (!analyses) return [];

    let rows = [...analyses];

    if (searchInput) {
      const search = searchInput.toLowerCase();
      rows = rows.filter(
        (row) =>
          row.name.toLowerCase().includes(search) ||
          row.brandName.toLowerCase().includes(search) ||
          row.query.toLowerCase().includes(search),
      );
    }

    rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return rows;
  }, [analyses, searchInput]);

  const totalCount = analyses?.length ?? 0;
  const hasSearch = Boolean(searchInput);
  const navigateToNewAnalysis = () => void navigate(ROUTES.IQ_NEW_ANALYSIS);

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <h2 className="text-xl font-medium tracking-tight text-foreground">
          {t('analysis.analysisListContent.dataFailedToLoad')}
        </h2>
        <p className="text-base text-muted-foreground">
          {t('analysis.analysisListContent.anErrorOccurredAndThe')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <ToolbarSkeleton />;
  }

  if (totalCount === 0 && !hasSearch) {
    return <AnalysisEmptyState />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AnalysisNotifications />
      <div
        className="flex items-center justify-between gap-2 p-section"
        data-testid="analysis-toolbar"
      >
        <div className="flex items-center gap-2">
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('analysis.analysisListContent.search')}
            aria-label={t('analysis.analysisListContent.searchLabel')}
            className="w-80"
          />
        </div>

        <div className="flex items-center gap-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />

          <Button size="default" onClick={navigateToNewAnalysis}>
            <PlusIcon className="size-4" />
            {t('analysis.analysisListContent.newIQ')}
          </Button>
        </div>
      </div>

      {/* Owns the scroll: ListPageBody is overflow-hidden, so the results clip without it. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-section">
        {filteredRows.length === 0 && hasSearch ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <h2 className="text-xl font-medium tracking-tight text-foreground">
              {t('analysis.analysisListContent.noMatchingAnalyses')}
            </h2>
            <p className="text-base text-muted-foreground">
              {t('analysis.analysisListContent.noAnalysesMatchYourCurrent')}
            </p>
            <Button variant="outline" size="default" onClick={() => setSearchInput('')}>
              {t('analysis.analysisListContent.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {viewMode === 'list' ? (
              <AnalysisListView analyses={filteredRows} />
            ) : (
              <AnalysisGridView analyses={filteredRows} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
